import { useEffect, useMemo, useState } from "react";
import type {
  SearchBoxAdministrativeUnitTypes,
  SearchBoxRetrieveResponse,
  SearchBoxSuggestion,
} from "@mapbox/search-js-core";
import { SessionToken } from "@mapbox/search-js-core";
import { useSearchBoxCore } from "@mapbox/search-js-react";
import { debounce } from "lodash";
import { useFormContext } from "react-hook-form";
import { useCurrentCommunityMember } from "@/react/contexts/punditUserContext";
import { useMapboxToken } from "./useMapboxToken";

const sessionToken = new SessionToken();
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const formattedNameForSuggestion = (suggestion: SearchBoxSuggestion) =>
  [suggestion.name, suggestion.place_formatted].filter(Boolean).join(", ");

const findSuggestionByMapboxId = (
  suggestions: SearchBoxSuggestion[] | undefined,
  mapboxId: string,
) => suggestions?.find(suggestion => suggestion.mapbox_id === mapboxId);

const formatLocationOptions = (
  suggestions: SearchBoxSuggestion[] | undefined,
) =>
  suggestions?.map(suggestion => ({
    type: "mapbox_v6",
    text: suggestion.name,
    value: suggestion.mapbox_id,
    data: suggestion,
    description: formattedNameForSuggestion(suggestion),
  }));

const formattedNameForSelectedLocation = (
  location: SearchBoxRetrieveResponse,
) => location.features[0].properties.full_address;

export type LocationType = SearchBoxAdministrativeUnitTypes;

export interface LocationOption {
  description: string;
  label: string;
  location?: SearchBoxRetrieveResponse;
  suggestion?: SearchBoxSuggestion;
  value: string;
}

interface UseMapboxProps {
  initialValue?: LocationValue;
  locationTypes?: SearchBoxAdministrativeUnitTypes[];
  name: string;
  shouldSkipLocationDetailRetrieve?: boolean;
}

const DEFAULT_LOCATION_TYPES: SearchBoxAdministrativeUnitTypes[] = [
  "country",
  "region",
  "district",
  "place",
  "locality",
  "neighborhood",
];

export interface LocationValue {
  data: SearchBoxRetrieveResponse | SearchBoxSuggestion | null;
  description?: string;
  text: string;
  type: string;
  value: string;
}

const isSearchBoxSuggestion = (
  data: SearchBoxRetrieveResponse | SearchBoxSuggestion,
): data is SearchBoxSuggestion => "mapbox_id" in data;

const valueFromSelectedLocation = (location: SearchBoxRetrieveResponse) =>
  location.features[0].properties.mapbox_id;

// Mapbox can be used for filtering and for location selection.
// When used for filtering, we only need to retrieve the location name and mapbox_id.
// When used for location selection, we need to retrieve the location name, mapbox_id and location details.
// This will also change how values are displayed and handled.
export const useMapbox = ({
  initialValue,
  shouldSkipLocationDetailRetrieve = false,
  locationTypes = DEFAULT_LOCATION_TYPES,
  name,
}: UseMapboxProps) => {
  const [suggestions, setSuggestions] = useState<SearchBoxSuggestion[]>([]);

  const [isCleared, setIsCleared] = useState(false);
  const [selectedLocation, setSelectedLocation] =
    useState<SearchBoxRetrieveResponse | null>(null);

  const [selectedSuggestion, setSelectedSuggestion] =
    useState<LocationValue | null>(initialValue ?? null);

  const currentCommunityMember = useCurrentCommunityMember();
  const language = currentCommunityMember?.locale || "en";
  const { setValue, getValues } = useFormContext();
  const { data: mapBoxData, refetch: refetchMapboxToken } = useMapboxToken();

  const currentValue: LocationValue | null = getValues(name);
  const searchBoxCore = useSearchBoxCore({
    accessToken: mapBoxData?.token,
  });

  const handleClearLocation = () => {
    setSelectedSuggestion(null);
    setSelectedLocation(null);
    setSuggestions([]);
    setIsCleared(true);
    setValue(
      name,
      {
        type: "mapbox_v6",
        text: "",
        data: {
          place_formatted: null,
          name: "",
          mapbox_id: null,
        },
        value: "",
      },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string, retryCount = 0) => {
        if (value) {
          try {
            const response = await searchBoxCore.suggest(value, {
              sessionToken,
              language,
              types: locationTypes.join(","),
            });
            setSuggestions(response.suggestions);
          } catch (error: unknown) {
            if (
              typeof error === "object" &&
              error !== null &&
              "statusCode" in error &&
              error.statusCode === 401 &&
              retryCount < 3
            ) {
              await delay(retryCount * 1000);
              await refetchMapboxToken();
              await debouncedSearch(value, retryCount + 1);
            } else {
              throw error;
            }
          }
        } else {
          setSuggestions([]);
        }
      }, 300),
    [searchBoxCore, language, locationTypes, refetchMapboxToken],
  );

  const handleSuggestionSelect = async (option: LocationValue | null) => {
    if (!option) {
      setSuggestions([]);
      setSelectedLocation(null);
      return;
    }

    try {
      if (shouldSkipLocationDetailRetrieve) {
        setSelectedSuggestion(option);
        setSelectedLocation(null);
        setSuggestions([]);
        return;
      }

      const userSelectedSuggestion = findSuggestionByMapboxId(
        suggestions,
        option.value,
      );
      if (!userSelectedSuggestion) {
        throw new Error("Suggestion not found");
      }
      const response = await searchBoxCore.retrieve(userSelectedSuggestion, {
        sessionToken,
        language,
      });
      setSelectedLocation(response);
      setSelectedSuggestion(null);
      setSuggestions([]);

      return;
    } catch (error) {
      console.error(error);
      setSelectedLocation(null);
      setSelectedSuggestion(null);
      setSuggestions([]);
    }
  };

  const locationOptions: LocationValue[] | undefined =
    formatLocationOptions(suggestions);

  const displayValue = (option: LocationValue | null): string => {
    if (option?.data) {
      if (isSearchBoxSuggestion(option.data)) {
        return formattedNameForSuggestion(option.data);
      }
      return formattedNameForSelectedLocation(option.data);
    }

    return isCleared ? "" : initialValue?.text || "";
  };

  // Based on different views, we either will have a selectedLocation or a selectedSuggestion.
  // We need to handle both cases.
  const value = useMemo(() => {
    if (selectedLocation) {
      return {
        type: "mapbox_v6",
        text: formattedNameForSelectedLocation(selectedLocation),
        data: selectedLocation,
        value: valueFromSelectedLocation(selectedLocation),
        description: formattedNameForSelectedLocation(selectedLocation),
      };
    }
    if (selectedSuggestion) {
      return {
        type: "mapbox_v6",
        text: selectedSuggestion.text,
        data: selectedSuggestion.data,
        value: selectedSuggestion.value,
        description: selectedSuggestion.description,
      };
    }

    return null;
  }, [selectedLocation, selectedSuggestion]);

  useEffect(() => {
    // We are controlling the value of the input field with the value hook
    // When the value changes, we need to update the input field
    if (value?.text !== currentValue?.text) {
      setValue(
        name,
        {
          type: "mapbox_v6",
          text: value?.text ?? "",
          data: value?.data,
        },
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
    }
  }, [value, name, setValue, currentValue?.text]);

  return {
    handleClearLocation,
    locationOptions,
    handleSuggestionSelect,
    value,
    debouncedSearch,
    hasNoSuggestions: suggestions?.length === 0,
    displayValue,
  };
};
